/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import * as Sentry from "@sentry/react"
import {
  ABORT_PHASE_TAG,
  BODY_DEADLINE_TAG,
  abortPhaseOf,
  bodyDeadlineDetailsOf,
} from "services/fetching/requestContext"

/**
 * Request header names kept on an outgoing event, lower-cased for a case-insensitive match.
 */
const RETAINED_REQUEST_HEADER_NAMES: readonly string[] = ["user-agent"]

/**
 * Reduces an event's request headers to the ones we have decided are safe to send, or undefined
 * when none of them are - in which case the caller removes the object entirely rather than
 * sending an empty one.
 *
 * `User-Agent` is the only header Sentry parses server-side into the `browser`, `os` and
 * `device` contexts and their tags, and it is the only one on this list. Nothing else the SDK
 * attaches is read for anything: `Referer` is displayed verbatim, and can carry query-string PII
 * from the referring page, so it is dropped along with everything unrecognised.
 */
// AIDEV-NOTE (#972): an allowlist, deliberately, and not `delete event.request.headers.Referer`.
// The predecessor deleted the whole headers object as defence-in-depth against a future
// integration attaching a `Cookie` or `Authorization` header - a real risk, since this hook is
// the single gate on what leaves the browser - and took the `User-Agent` with it, so every
// frontend event for two weeks reached Sentry with no browser, OS or device, and nothing
// warned. Naming what is *kept* rather than what is dropped is what makes that unrepeatable: a
// header nobody has reasoned about is discarded by default, so the collateral damage of the
// next SDK or integration change is zero rather than silent.
//
// The User-Agent reaches this hook only because SENTRY_DATA_COLLECTION's header filter lets it
// through. `getHttpRequestData` builds `{ Referer, 'User-Agent' }`
// (node_modules/@sentry/browser/build/npm/cjs/prod/helpers.js:110), and `httpContextIntegration`,
// a default integration, runs it through `dataCollection.httpHeaders.request` in
// `preprocessEvent` (integrations/httpcontext.js:14-25) - before beforeSend, so a header that
// filter removes is gone before this allowlist ever sees it. The deny list below matches neither
// header name, nor do the SDK's always-on sensitive-key snippets
// (@sentry/core build/cjs/utils/data-collection/filtering-snippets.js:4-26), so both arrive here
// and this allowlist then drops the Referer. Setting `httpHeaders.request` to `false` would lose
// the User-Agent with no error; sentry.test.ts captures a real event to catch exactly that.
// Sentry does not classify the User-Agent as PII: it is a low-cardinality string shared by
// everyone on that browser build, not an identifier.
//
// Matched case-insensitively because HTTP header names are, even though the SDK spells this one
// `User-Agent`; a manual capture call setting `user-agent` should not silently lose it.
const retainedRequestHeaders = (
  headers: Readonly<Record<string, string>> | undefined
): Record<string, string> | undefined => {
  if (!headers) {
    return undefined
  }
  const retainedNames = Object.keys(headers).filter(name =>
    RETAINED_REQUEST_HEADER_NAMES.includes(name.toLowerCase())
  )
  return retainedNames.length > 0
    ? Object.fromEntries(retainedNames.map(name => [name, headers[name]]))
    : undefined
}

/**
 * What the SDK may collect: the baseline @sentry's v11 migration guide gives for keeping the v10
 * default, under "If you want to keep the v10 default behavior", plus v10's 7 context lines.
 */
// AIDEV-NOTE (#959): every key whose default v11 changed is pinned, because leaving
// `dataCollection` unset is not the restrictive default it was. The one key left out,
// `stackFrameVariables`, defaults to `true` in v10 and v11 alike (the guide's comparison table)
// and nothing in the browser packages reads it. v11 replaced `sendDefaultPii` with `dataCollection` and defaults
// every category to collect (@sentry/core
// build/cjs/utils/data-collection/resolveDataCollectionOptions.js:3-15): user info, cookies,
// unfiltered headers, every HTTP body, 5 lines of frame context. The 9.40.0 SDK this replaced
// collected no user info, cookies or bodies by default and scrubbed headers, and this keeps that
// posture on purpose; adopting any v11 default is a separate decision, not a side effect of the
// upgrade.
//
// `userInfo: false` is the one #959 is about. With it the browser client sets
// `sdk.settings.infer_ip = "never"` (@sentry/browser build/npm/cjs/prod/client.js:23-31), which
// travels inside every error and transaction event's own `sdk` field (@sentry/core
// build/cjs/envelope.js:7-23) - session envelopes carry no settings - and it does not stamp
// sessions with `{{auto}}` (client.js:69-71).
// The client now asks Sentry not to infer the IP; the org-level "Prevent Storing of IP
// Addresses" setting is still what guarantees it. Relay honouring `infer_ip` is the vendor's
// documentation, which this repo has not tested.
//
// The object is copied verbatim from the guide, deny list repeated, so it can be checked against
// it line by line. `frameContextLines` is read only by `contextLinesIntegration`
// (integrations/contextlines.js:12), which is not a default integration (sdk.js:17-32), so it
// changes nothing today; it is pinned so adding that integration later gets 7 lines, not 5.
export const SENTRY_DATA_COLLECTION: Readonly<
  NonNullable<Sentry.BrowserOptions["dataCollection"]>
> = {
  userInfo: false,
  cookies: false,
  httpHeaders: {
    request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
  },
  httpBodies: [],
  urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  graphQL: { document: false, variables: false },
  frameContextLines: 7,
}

/**
 * How spans are sent: as transactions once the local span tree completes, as 9.40.0 did.
 */
// AIDEV-NOTE: v11 defaults to `'stream'` (@sentry/core build/cjs/client.js:101), under which a
// root span produces no transaction event and every fetch with no active span gets a root
// `http.client` span of its own (fetch.js:43-44). The #877 measurement in
// services/fetching/initialScrs.ts, and the test that guards it, are built on transactions.
// Kept on purpose; moving to streaming is a separate decision. Exported so that test's capturing
// client runs under the same lifecycle as production.
export const SENTRY_TRACE_LIFECYCLE: NonNullable<Sentry.BrowserOptions["traceLifecycle"]> = "static"

/**
 * Default integrations v11 added that 9.40.0 had no equivalent of, by integration name.
 */
// AIDEV-NOTE: excluded because each is new behaviour, not 9.40.0 behaviour moved; adopting either
// is a separate decision. `CultureContext` adds the user's locale, timezone and calendar to every
// event (@sentry/browser build/npm/cjs/prod/integrations/culturecontext.js:10-17).
// `ConversationId` tags gen_ai spans with a conversation id (@sentry/core
// build/cjs/integrations/conversationId.js:13-24); nothing here sets one, so it is inert today,
// and excluding it keeps it so. The third v11 addition, `Console`, is kept: it is 9.40.0's
// console breadcrumbs moved out of `breadcrumbsIntegration`, with the same seven levels.
//
// Excluded by name from the defaults the SDK passes in, rather than by replacing
// `defaultIntegrations` with a hand-written list, so every other default - including any a
// future version adds - still arrives without this file having to name it. sentry.test.ts pins
// the resulting set against the installed SDK's real defaults, so a new default, or an excluded
// one returning under another name, fails a test rather than shipping silently.
const EXCLUDED_DEFAULT_INTEGRATION_NAMES: readonly string[] = ["ConversationId", "CultureContext"]

// @sentry/react does not re-export @sentry/core's `Integration` type; this is the same type.
type Integration = Parameters<typeof Sentry.addIntegration>[0]

/**
 * The integrations to run: the SDK's defaults without the excluded ones, with three of our own
 * configured in place of any default of the same name.
 */
// AIDEV-NOTE: the configured three, each kept at 9.40.0 behaviour on purpose; adopting a v11
// default instead is a separate decision.
// - `browserSessionIntegration` pins `lifecycle: "route"`, a new session on every navigation as
//   9.40.0 did; v11 defaults to `"page"`, one per page load (@sentry/browser
//   build/npm/cjs/prod/integrations/browsersession.js:9). Its default instance is dropped here
//   rather than left to the SDK's de-duplication, so this list is the set the SDK sets up. One
//   more arrives after it: `browserTracingIntegration` adds `WebVitals` in its `afterAllSetup`
//   (@sentry/browser build/npm/cjs/prod/tracing/browserTracingIntegration.js:252-260), 9.40.0's
//   CLS, LCP and INP tracking moved out of browserTracingIntegration into its own integration.
// - `userTimingIntegration` restores `performance.mark`/`measure` spans, which 9.40.0's
//   `browserTracingIntegration` recorded and v11's no longer does. Like 9.40.0 it attaches them
//   to pageload and navigation spans as they end and skips entries older than a navigation's
//   start (@sentry/browser-utils build/cjs/performance/userTiming.js:18-44), and `ignore: []`
//   matches 9.40.0's default `ignorePerformanceApiSpans: []`. Two differences the API does not
//   let us remove: the spans' `sentry.origin` is `auto.browser.user_timing.<type>` rather than
//   `auto.resource.browser.metrics`, and React 19's "Components ⚛" measures are always skipped
//   (userTiming.js:51-80) - which React's production build does not emit.
const selectIntegrations = (defaults: readonly Readonly<Integration>[]): Integration[] => {
  const configured: readonly Readonly<Integration>[] = [
    Sentry.browserTracingIntegration({
      // v11 starts a root navigation span when the page is restored from the back/forward cache
      // (`pageshow` with `persisted`; @sentry/browser
      // build/npm/cjs/prod/tracing/browserTracingIntegration.js:302-329, default `true` at :19).
      // 9.40.0 had no such handling, so this is kept off on purpose; adopting it is a separate
      // decision (#1507).
      instrumentBfcacheRestore: false,
    }),
    Sentry.browserSessionIntegration({ lifecycle: "route" }),
    Sentry.userTimingIntegration({ ignore: [] }),
  ]
  const configuredNames = configured.map(integration => integration.name)
  return [
    ...defaults.filter(
      integration =>
        !EXCLUDED_DEFAULT_INTEGRATION_NAMES.includes(integration.name) &&
        !configuredNames.includes(integration.name)
    ),
    ...configured,
  ]
}

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string

  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn("Sentry DSN not configured. Error tracking disabled.")
    return
  }

  Sentry.init({
    dsn,

    integrations: selectIntegrations,

    traceLifecycle: SENTRY_TRACE_LIFECYCLE,

    // v11 defaults this to true (@sentry/core build/cjs/client.js:99), which gives captureMessage
    // and non-Error captures a synthetic stack and so regroups those issues. Kept at 9.40.0's
    // false on purpose; adopting the new default is a separate decision.
    attachStacktrace: false,

    // Debug logging (only in development)
    debug: !import.meta.env.PROD,

    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Release tracking
    release: (import.meta.env.VITE_APP_VERSION as string) || "unknown",

    // Environment
    environment: import.meta.env.MODE,

    // AIDEV-NOTE: #959 - declared rather than inherited from a default, the criterion #343
    // left unmet, and in v11 no longer redundant: unset, the SDK collects user info, cookies
    // and HTTP bodies. See SENTRY_DATA_COLLECTION for what each key keeps, and for why the
    // org-level IP setting, not this option, is what keeps the address out of Sentry.
    dataCollection: SENTRY_DATA_COLLECTION,

    // Filter out errors that are not actionable
    // AIDEV-NOTE: EventHint is Sentry's own type and is not declared readonly; only
    // `originalException` is read here, and the event itself is mutated below by design -
    // beforeSend's contract is to return a modified event.
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    beforeSend(event, hint) {
      // Filter out network errors that might be due to ad blockers or connectivity issues
      if (event.exception) {
        const error = event.exception.values?.[0]
        if (error?.type === "NetworkError" || error?.type === "ChunkLoadError") {
          return null
        }
      }

      // Filter out errors from browser extensions
      // Note: Uses includes() rather than startsWith() to be overly cautious.
      // False positive risk is negligible; prevents extension error noise in dashboard.
      if (event.request?.url?.includes("extension://")) {
        return null
      }

      // Defense-in-depth PII scrub: SENTRY_DATA_COLLECTION turns off cookies and user info, so
      // the SDK should not populate these fields, but strip them if a future change
      // (integration, manual capture call, a loosened dataCollection) ever adds them.
      if (event.request) {
        delete event.request.cookies
        const retainedHeaders = retainedRequestHeaders(event.request.headers)
        if (retainedHeaders) {
          event.request.headers = retainedHeaders
        } else {
          delete event.request.headers
        }
      }
      if (event.user) {
        delete event.user.ip_address
      }

      // AIDEV-NOTE (#617): the phase an aborted fetch stalled in - waiting for response
      // headers, or midway through the body - is knowable only inside the fetch that armed the
      // two deadlines, so it travels on the error itself and is turned into a tag here. Doing
      // it centrally rather than at each capture site means every reporter of such an error
      // gets the tag, including ones that know nothing about fetch phases, and no capture site
      // can forget it. A tag rather than an `extra` because only tags are indexed: #843 needs
      // to *search* for the phase of the next occurrence, not read it off one event.
      const abortPhase = abortPhaseOf(hint.originalException)
      if (abortPhase !== null) {
        event.tags = { ...event.tags, [ABORT_PHASE_TAG]: abortPhase }
      }

      // AIDEV-NOTE (#1554): the same pattern for what a body-phase abort had seen. Which
      // deadline fired is a tag, because "were these stalls or slow downloads" is a question
      // asked across events and only tags are searchable. The counts are `extra`: they are read
      // off one event at a time, and as tags each distinct byte count would be its own value.
      // All three extras are written even when null, so "unknown" (no Content-Length, or a body
      // that could not be counted) reads differently from "this was not a body-phase abort".
      const bodyDeadline = bodyDeadlineDetailsOf(hint.originalException)
      if (bodyDeadline !== null) {
        event.tags = { ...event.tags, [BODY_DEADLINE_TAG]: bodyDeadline.deadline }
        event.extra = {
          ...event.extra,
          bodyBytesReceived: bodyDeadline.bytesReceived,
          bodyMsSinceHeaders: bodyDeadline.msSinceHeaders,
          bodyExpectedBytes: bodyDeadline.expectedBytes,
        }
      }

      return event
    },

    // Configure user context
    initialScope: {
      tags: {
        component: "frontend",
      },
    },
  })
}

// Export Sentry for manual error capturing
export { Sentry }
